
/*
PLEASE DON'T CANCEL THIS COMMENT, IT'S USEFUL FOR FUTURE MAINTENANCE

FIGMA
https://www.figma.com/design/ZLHeAm6skKQffNbvAZegIT/SGH-%7C-RTR?node-id=66-111&node-type=canvas&m=dev

DOC
https://abstractsrl.atlassian.net/browse/LXSGH21R-14355
*/

import { algoliaGetRule } from '@libs/utils/algoliaIntegration'
import isEmpty from 'lodash/isEmpty'
import RtrBannerInfo from '@components/rtr-banner/components/rtr-banner-info.vue'
import { getAlgoliaProductNormalized } from '@libs/algolia-normalizer'
import { initializeRtrScript } from '@libs/utils/rtr'
import IntersectionObserverComponent from '@directives/intersectionObserver.vue'
import { loadSectionTranslations } from '@/scripts/i18n-setup.js'
import { loadExternalLibraryAsync } from '@libs/utils/utils'
import { tryParseJSON } from '@libs/utils/string'
import {fetchRtrGlbUrl, prefetchGlbAndScript} from '@libs/utils/rtr-banner-prefetch'

export default {
  name: 'RtrBanner',
  components: {
    RtrBannerInfo,
    IntersectionObserverComponent,
  },
  props: {
    conf: {
      type: String,
      default: '',
    },
    rule: {
      type: String,
      default: 'RTR_HP_BANNER',
    },
    bannerTitle: {
      type: String,
      default: 'See our best seller in 3D',
    },
    buttonCategory: {
      type: String,
      default: 'primary',
    },
    textColorClass: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      data: [],
      isLoaded: false,
      rtrProductIndex: 0,
      rtrPrefetchProductIndex: 0,
      showRtrCursor: false,
      isCube: false,
    }
  },
  computed: {
    rtrViewerSrc() {
      return (
        sessionStorage.getItem('rtrScriptUrl') ||
        'https://rtr-viewer.luxottica.com/lib/v/6.0.0/main.js'
      )
    },
    backgroundPath() {
      let parsedConf = {}

      try {
        const jsonString = this.conf.replace(/'/g, '"')
        parsedConf = JSON.parse(jsonString)
      } catch (error) {
        console.error('Error in parsing di this.conf:', error)
      }

      return this.isMobile ? parsedConf.backgroundMob : parsedConf.backgroundDesk
    },
    selectedIndex() {
      return this.$refs.RtrBannerInfo.selectedIndex
    },
    isMobile() {
      return this.$mq === 'tablet' || this.$mq === 'tabletLandscape'
    },
    isWhiteColor() {
      return this.textColorClass === 'cms-custom-text-color-white'
    },
    boundingAreaSettings() {
      return {
        topLeft: {
          x: 0,
          y: 0,
        },
        bottomRight: {
          x: 1,
          y: this.isMobile ? 0.6 : 1,
        },
      }
    },
  },
  watch: {
    rtrPrefetchProductIndex(index) {
      this.prefetchRtrData(this.data[index].upc)
    },
  },
  mounted() {
    loadSectionTranslations('cms')
  },
  methods: {
    async getAlgoliaProducts() {
      return await algoliaGetRule(this.rule, 4).then(result => {
        if (!isEmpty(result.hits)) {
          const { hits } = result
          return hits?.map(product => {
            let item = getAlgoliaProductNormalized(product, false, 'rtr-wall')
            return {
              upc: item.upc,
              img: item.img,
              productName: item.modelName,
              productBrand: item.brand,
              pdpLink: item.URL,
            }
          })
        }
      })
    },
    async getAvailableBannerProductsData() {
      await loadExternalLibraryAsync({
        src: this.rtrViewerSrc,
        crossOrigin: 'anonymous',
      })
      const rtrAvailabilityPromises = this.data?.map(hit => this.checkUpcIsAvailableForRtr(hit.upc))
      const bannerProductsAvailabilityData = await Promise.all(rtrAvailabilityPromises)
      return this.data?.filter(
        (_, index) => bannerProductsAvailabilityData[index][this.data[index].upc]
      )
    },
    async initRtr(upc) {
      const parsedRtrConf = tryParseJSON(this.conf)
      this.isCube = true
      const {
        backgroundDesk = null,
        backgroundMob = null,
        boundingAreaDesktop = {
          topLeft: { x: 0, y: 0 },
          bottomRight: { x: 1, y: 1 },
        },
        boundingAreaMobile = {
          topLeft: { x: 0, y: 0 },
          bottomRight: { x: 1, y: 0.6 },
        },
        cameraRotationInitial = { phi: 0, theta: 0 },
        cameraRotationReset = { phi: 30, theta: 30 },
        autoResetCamera = false,
        scrollInteraction = true,
        autoRotateCamera = false,
        orbitpoint = false,
        clearColor = '#f7f7f7',
      } = parsedRtrConf

      const settings = {
        boundingArea: this.isMobile ? boundingAreaMobile : boundingAreaDesktop,
        cameraRotationInitial: cameraRotationInitial,
        cameraRotationReset: cameraRotationReset,
        autoResetCamera: autoResetCamera,
        scrollInteraction: true,
        autoRotateCamera: autoRotateCamera,
        orbitPoint: orbitpoint,
        showBackground: this.isMobile & backgroundMob || backgroundDesk ? true : false,
        clearColor: clearColor,
      }

      if (!scrollInteraction) {
        settings.gestures = { mouse: { wheel: 'none' } }
      }

      await initializeRtrScript({
        src: this.rtrViewerSrc,
        conf: {
          data: {
            id: {
              type: 'upc',
              value: upc,
            },
            selector: '#rtr-3d-container',
            backgroundPath: this.isMobile ? backgroundMob : backgroundDesk,
            settings: settings,
          },
          callbacks: {
            onRendered: () => {
              this.isLoaded = true
              this.isCube = false
            },
          },
        },
        type: 'viewer',
      }).catch(error => {
        console.error('Error initializing RTR script', error)
      })
    },
    changeIndex(index) {
      window.rtrViewer?.setId({
        type: 'upc',
        value: this.data[index].upc,
      })
    },
    changeRtrPrefetchProductIndex(index) {
      this.rtrPrefetchProductIndex = index
    },
    async initBanner() {
      try {
        await this.getAlgoliaProducts().then(data => {
          this.data = data
          this.prefetchRtrData(data[0].upc)
        })
        const filteredData = await this.getAvailableBannerProductsData()
        this.data = filteredData
        await this.initRtr(filteredData[this.rtrProductIndex]?.upc)
      } catch (error) {
        console.error('Error initializing banner:', error)
      }
    },
 
    async prefetchRtrData(upc) {
  const glbUrl = await fetchRtrGlbUrl(upc)
  prefetchGlbAndScript(glbUrl, null)
},
    async checkUpcIsAvailableForRtr(upc) {
      try {
        const isUpcAvailable = await window.rtrViewer?.isIdAvailable(
          {
            type: 'upc',
            value: upc,
          },
          {
            env: 'production',
            qa: false,
          }
        )
        return { [upc]: isUpcAvailable }
      } catch (err) {
        return { [upc]: false }
      }
    },
  },
}
